/**
 * Screens 06 «Новая запись», 07 «Время занято» and 08 «Карточка записи»
 * (salon-mockup.html newbook / conflict / apdetail; doc 10 §4-5).
 *
 * 06 and 08 sit over the day's timeline, as the mockup draws them: a bottom
 * sheet on a phone, a modal (06) or the right-hand pane (08) on desktop.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { ApiError, api } from '../core/api';
import { AppBar, Sheet, Shell } from '../components/Shell';
import { Timeline } from '../components/Timeline';
import { toast } from '../components/Toast';
import { Icon } from '../components/icons';
import { errorText, formatMoney } from '../i18n';
import { addDays, dateWithWeekday, dayTitle, dayTitleShort, hm, instant, ymd } from '../lib/time';
import { fullPhone, groupLocal, localDigits } from '../lib/phone';
import { serviceName } from '../lib/serviceName';
import { allowedNext } from '../lib/status';
import { useDesktop } from '../lib/useDesktop';
import type { Appointment, AppointmentStatus, Client, Conflict, Locale, Service } from '../types';

// ---------------------------------------------------------------------------
// shared

function useRefreshCalendar() {
  const qc = useQueryClient();
  return () => {
    for (const key of ['today', 'day', 'week', 'month', 'appointment']) void qc.invalidateQueries({ queryKey: [key] });
  };
}

function useWarn() {
  const { t } = useTranslation();
  return (appt: Appointment) => {
    // D-48: saved first, then one toast.
    if (appt.warnings.includes('DURING_TIME_OFF')) toast(t('book.duringBreak'));
    else if (appt.warnings.includes('OUTSIDE_WORKING_HOURS')) toast(t('book.outsideHours'));
  };
}

/** The day behind a sheet: its timeline, full width. */
function DayBackdrop({ date, title, side }: { date: string; title: string; side?: ReactNode }) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['day', date], queryFn: () => api.day(date) });
  return (
    <>
      <AppBar title={title} />
      <div className="content">
        <div className="pane pane-wide">
          {q.data && (
            <Timeline
              day={q.data}
              onOpen={(id) => navigate(`/appointments/${id}`)}
              onFree={(start) => navigate(`/appointments/new?start=${encodeURIComponent(start)}`)}
            />
          )}
        </div>
        {side}
      </div>
    </>
  );
}

interface Clash {
  conflicts: Conflict[];
  nearest: string | null;
  starts: string;
  ends: string;
  /** Repeat the save with overlap_ack, or at another start. */
  retry: (opts: { ack?: boolean; start?: string }) => Promise<void>;
}

function clashFrom(err: unknown, starts: string, ends: string, retry: Clash['retry']): Clash | null {
  if (!(err instanceof ApiError) || err.code !== 'OVERLAP') return null;
  const d = err.details as { conflicts?: Conflict[]; nearest_free_start?: string | null } | undefined;
  return { conflicts: d?.conflicts ?? [], nearest: d?.nearest_free_start ?? null, starts, ends, retry };
}

/** 07 «Время занято»: names who is in the way, never "error" (doc 10 B4-B7). */
function ConflictSheet({ clash, onClose }: { clash: Clash; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const run = async (opts: { ack?: boolean; start?: string }) => {
    setBusy(true);
    try {
      await clash.retry(opts);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onClose} className="narrow">
      <h3>{t('conflict.title')}</h3>
      <div className="alert">
        <b>{t('conflict.headline', { range: `${hm(clash.starts)}–${hm(clash.ends)}` })}</b>
        <span>{t('conflict.why')}</span>
      </div>
      <span className="cap">{t('conflict.blocking')}</span>
      {clash.conflicts.map((c) => (
        <div key={c.id} className="conflictbox">
          <div className="row">
            <span className="av">{c.client_name.charAt(0)}</span>
            <span className="grow">
              <span className="nm">{c.client_name}</span>
              <span className="sm ell" style={{ display: 'block' }}>
                {serviceName(c.service_name_ru, c.service_name_ky, i18n.language)} · {minutesBetween(c.starts_at, c.ends_at)} {t('dur.min')}
              </span>
            </span>
            <b className="tnum" style={{ color: 'var(--ink)', fontSize: 14 }}>
              {hm(c.starts_at)}–{hm(c.ends_at)}
            </b>
          </div>
        </div>
      ))}
      <span className="cap">{t('conflict.options')}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 16 }}>
        {clash.nearest && (
          <button type="button" className="btn" disabled={busy} onClick={() => run({ start: clash.nearest! })}>
            {t('conflict.nearest', { time: hm(clash.nearest) })}
          </button>
        )}
        <button type="button" className="btn" disabled={busy} onClick={onClose}>
          {t('conflict.other')}
        </button>
      </div>
      <button type="button" className="btn dgr blk" disabled={busy} onClick={() => run({ ack: true })}>
        {t('conflict.anyway')}
      </button>
      <p className="xs" style={{ margin: '10px 0 0', textAlign: 'center' }}>
        {t('conflict.hatch')}
      </p>
    </Sheet>
  );
}

function minutesBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 60_000);
}

export function nextFiveMinutes(now: Date): string {
  // D-47: a time "from now" is rounded up to the next 5 minutes.
  const ms = 5 * 60_000;
  return hm(new Date(Math.ceil(now.getTime() / ms) * ms));
}

// ---------------------------------------------------------------------------
// 06 «Новая запись» (also «Перенести» and «Повторить» from 08)

// D-73: a name nobody has is a new client, made by «Записать» together with
// the visit -- «Новый клиент» no longer creates on click.
function ClientPicker({ value, onChange, q, onQ, isNew, onNew }: {
  value: Client | null;
  onChange: (c: Client | null) => void;
  q: string;
  onQ: (q: string) => void;
  isNew: boolean;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const name = q.trim();
  const found = useQuery({
    queryKey: ['clients', q],
    queryFn: () => api.clients(q),
    enabled: !value && !isNew && name.length > 0,
  });
  if (value) {
    return (
      <div className="inp" role="button" tabIndex={0} onClick={() => onChange(null)}>
        <span className="av" style={{ width: 24, height: 24, fontSize: 11 }}>
          {value.name.charAt(0)}
        </span>
        <span className="grow">{value.name}</span>
      </div>
    );
  }
  const items = found.data?.items ?? [];
  const listed = name && !isNew && items.length > 0;
  const fresh = name && (isNew || (found.data && items.length === 0));
  return (
    <>
      <input className="inp" placeholder={t('book.findClient')} value={q} onChange={(e) => onQ(e.target.value)} autoFocus />
      {listed && (
        <ul className="list">
          {items.slice(0, 6).map((c) => (
            <li key={c.id} onClick={() => onChange(c)}>
              <span className="av" style={{ width: 28, height: 28, fontSize: 12 }}>
                {c.name.charAt(0)}
              </span>
              <span className="grow">
                <span className="nm">{c.name}</span>
                {c.phone && <span className="sm" style={{ display: 'block' }}>{c.phone}</span>}
              </span>
            </li>
          ))}
          <li onClick={onNew}>
            <span className="grow" style={{ color: 'var(--accent)', fontWeight: 600 }}>
              {t('book.newClient', { name })}
            </span>
          </li>
        </ul>
      )}
      {fresh && (
        <p className="sm" style={{ margin: '6px 2px 0' }}>
          <b style={{ color: 'var(--accent)' }}>{t('book.newClient', { name })}</b> — {t('book.newClientHint')}
        </p>
      )}
    </>
  );
}

/** The grey «+996 │» in front of the phone digits (D-73). */
function PhonePrefix() {
  return <span style={{ color: 'var(--muted)', paddingRight: 10, borderRight: '1px solid var(--line-strong)' }}>+996</span>;
}

/** Stored «+996 700 98-76-54» shows behind the prefix; any other format as written. */
function CardPhone({ phone }: { phone: string }) {
  if (!phone.startsWith('+996')) return <span className="grow">{phone}</span>;
  return (
    <>
      <PhonePrefix />
      <span className="grow">{phone.slice(4).trim()}</span>
    </>
  );
}

// The mockup's inline icons for the 06 date and time boxes.
const calIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: 'var(--faint)', flex: '0 0 auto' }}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);
const clockIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: 'var(--faint)', flex: '0 0 auto' }}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export function NewBookingScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refresh = useRefreshCalendar();
  const warn = useWarn();
  const moveId = params.get('move');
  const moving = useQuery({ queryKey: ['appointment', moveId], queryFn: () => api.getAppointment(moveId!), enabled: !!moveId });

  const startParam = params.get('start');
  const [date, setDate] = useState(startParam ? ymd(startParam) : params.get('date') ?? ymd(new Date()));
  const [time, setTime] = useState(startParam ? hm(startParam) : nextFiveMinutes(new Date()));
  const services = useQuery({ queryKey: ['services'], queryFn: api.services });
  const [client, setClient] = useState<Client | null>(null);
  const [q, setQ] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [phone, setPhone] = useState(''); // the 9 digits after +996
  const [otherPerson, setOtherPerson] = useState(false);
  const qc = useQueryClient();
  const [service, setService] = useState<Service | null>(null);
  const [minutes, setMinutes] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [clash, setClash] = useState<Clash | null>(null);

  // Prefill: «Повторить» (client + service) and «Перенести» (the whole visit).
  const preClient = params.get('client');
  const preService = params.get('service') ?? moving.data?.service_id ?? null;
  useEffect(() => {
    const id = preClient ?? moving.data?.client_id;
    if (id && !client) void api.client(id).then(setClient);
  }, [preClient, moving.data, client]);
  useEffect(() => {
    const s = services.data?.find((x) => x.id === preService);
    // A move keeps the visit's own length and price, whichever answer lands first.
    if (s && !service) (moveId ? setService(s) : pick(s));
  }, [services.data, preService]);
  useEffect(() => {
    if (moving.data) {
      setDate(ymd(moving.data.starts_at));
      setTime(hm(moving.data.starts_at));
      setMinutes(String(minutesBetween(moving.data.starts_at, moving.data.ends_at)));
      setPrice(String(Number(moving.data.price)));
      setNotes(moving.data.notes ?? '');
    }
  }, [moving.data]);

  function pick(s: Service) {
    setService(s);
    setMinutes(String(s.duration_minutes));
    setPrice(String(Number(s.price))); // B2: filled, still editable
  }

  const done = (appt: Appointment) => {
    refresh();
    warn(appt);
    navigate(`/day/${ymd(appt.starts_at)}`, { replace: true });
  };

  // D-73: a typed phone already on a card books onto that card (06b), unless
  // «Это другой человек». Looked up as soon as all 9 digits are in.
  const phoneQuery = { queryKey: ['clients', 'phone', phone], queryFn: () => api.clients(phone) };
  const phoneHits = useQuery({ ...phoneQuery, enabled: !client && phone.length === 9 && !otherPerson });
  const ownerIn = (items: Client[] = []) => items.find((c) => localDigits(c.phone ?? '') === phone) ?? null;
  const owner = !client && phone.length === 9 && !otherPerson ? ownerIn(phoneHits.data?.items) : null;

  /** The card the visit goes on: picked, matched by phone, or made now. */
  async function cardFor(name: string): Promise<Client> {
    if (phone.length === 9 && !otherPerson) {
      const hit = ownerIn((await qc.fetchQuery(phoneQuery)).items);
      if (hit) return hit;
    }
    const card = await api.createClient({ name, phone: fullPhone(phone) });
    void qc.invalidateQueries({ queryKey: ['clients'] });
    return card;
  }

  async function save(opts: { ack?: boolean; start?: string; who?: Client } = {}) {
    const name = q.trim();
    let who = opts.who ?? client;
    if (!who && !name) return setError(t('book.pickClient'));
    if (!service) return setError(t('book.pickService'));
    const starts = opts.start ?? instant(date, time);
    const ends = new Date(Date.parse(starts) + Number(minutes) * 60_000).toISOString();
    setBusy(true);
    setError(null);
    try {
      if (!who) {
        who = await cardFor(name);
        setClient(who);
      }
      const appt = moveId
        ? await api.updateAppointment(moveId, { starts_at: starts, ends_at: ends, service_id: service.id, price, notes: notes || null, overlap_ack: !!opts.ack })
        : await api.createAppointment({ client_id: who.id, service_id: service.id, starts_at: starts, ends_at: ends, price, notes: notes || null, overlap_ack: !!opts.ack });
      setClash(null);
      done(appt);
    } catch (err) {
      // The retry from 07 books the card made above; it must not make a second one.
      const c = clashFrom(err, starts, ends, (o) => save({ ...o, who: who ?? undefined }));
      if (c) setClash(c); // B3: the form stays as it was underneath
      else setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save();
  };

  return (
    <Shell active="calendar">
      <DayBackdrop date={date} title={dayTitleShort(date, i18n.language as Locale)} />
      <Sheet onClose={() => navigate(-1)}>
        <form onSubmit={submit} noValidate>
          <h3>{t('book.title')}</h3>
          {error && <div className="warnbox form-error" role="alert">{error}</div>}
          <div className="fld">
            <label>{t('book.client')}</label>
            <ClientPicker
              value={client}
              onChange={setClient}
              q={q}
              onQ={(v) => {
                setQ(v);
                setIsNew(false);
              }}
              isNew={isNew}
              onNew={() => setIsNew(true)}
            />
          </div>
          <div className="fld">
            <label>
              {t('book.phone')} <span style={{ color: 'var(--faint)', fontWeight: 400 }}>· {t('book.optional')}</span>
            </label>
            {client ? (
              <div className="inp tnum" style={{ background: 'var(--surface-2)' }}>
                {client.phone ? <CardPhone phone={client.phone} /> : <span className="grow" style={{ color: 'var(--faint)' }}>{t('book.noPhone')}</span>}
                <span className="xs">{t('book.fromCard')}</span>
              </div>
            ) : (
              <div className="inp tnum">
                <PhonePrefix />
                <input
                  aria-label={t('book.phone')}
                  className="bare"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="700 00-00-00"
                  value={groupLocal(phone)}
                  onChange={(e) => {
                    const next = localDigits(e.target.value);
                    // A backspace over «-» or « » takes the digit before it.
                    setPhone(next === phone && e.target.value.length < groupLocal(phone).length ? phone.slice(0, -1) : next);
                    setOtherPerson(false);
                  }}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            )}
            {owner && (
              <div style={{ border: '1px solid var(--st-confirm-bd)', background: 'var(--st-confirm-bg)', color: 'var(--st-confirm-fg)', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginTop: 8, lineHeight: 1.4 }}>
                {t('book.phoneMatch', { name: owner.name })}{' '}
                <a role="button" tabIndex={0} style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setOtherPerson(true)}>
                  {t('book.otherPerson')}
                </a>
              </div>
            )}
          </div>
          <div className="fld">
            <label>{t('book.service')}</label>
            <div className="chips">
              {services.data?.map((s) => (
                <span key={s.id} role="button" tabIndex={0} className={`chip${service?.id === s.id ? ' on' : ''}`} onClick={() => pick(s)}>
                  {serviceName(s.name_ru, s.name_ky, i18n.language)}
                </span>
              ))}
            </div>
          </div>
          {/* D-73: two boxes, each with its own invisible native picker over the whole box. */}
          <div className="row" style={{ gap: 11, alignItems: 'flex-start' }}>
            <div className="fld grow">
              <label>{t('book.date')}</label>
              <div className="inp tnum" style={{ position: 'relative' }}>
                <span className="grow">{dateWithWeekday(date, t('book.weekdayInDate', { returnObjects: true }) as string[])}</span>
                {calIcon}
                <input aria-label={t('book.date')} className="picker" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} style={{ left: 0, width: '100%' }} />
              </div>
            </div>
            <div className="fld grow">
              <label>{t('book.time')}</label>
              <div className="inp tnum" style={{ position: 'relative' }}>
                <span className="grow">{time}</span>
                {clockIcon}
                <input aria-label={t('book.time')} className="picker" type="time" step={300} value={time} onChange={(e) => e.target.value && setTime(e.target.value)} style={{ left: 0, width: '100%' }} />
              </div>
            </div>
          </div>
          <div className="row" style={{ gap: 11, alignItems: 'flex-start', marginTop: 0 }}>
            <div className="fld grow">
              <label>{t('book.duration')}</label>
              <div className="inp">
                <input aria-label={t('book.duration')} className="bare" type="number" min={5} step={5} value={minutes} onChange={(e) => setMinutes(e.target.value)} style={{ width: `${Math.max(minutes.length, 2) + 1}ch` }} />
                <span>{t('dur.min')}</span>
              </div>
            </div>
            <div className="fld grow">
              <label>{t('book.price')}</label>
              <div className="inp">
                <input aria-label={t('book.price')} className="bare" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: `${Math.max(price.length, 2) + 1}ch` }} />
                <span>{t('money.som')}</span>
              </div>
            </div>
          </div>
          <div className="fld">
            <label>
              {t('book.note')}
              <input className="inp" placeholder={t('book.optional')} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </div>
          <div className="btnrow" style={{ marginTop: 4 }}>
            <button type="button" className="btn" onClick={() => navigate(-1)}>
              {t('book.cancel')}
            </button>
            <button type="submit" className="btn pri" disabled={busy}>
              {t('book.save')}
            </button>
          </div>
        </form>
      </Sheet>
      {clash && <ConflictSheet clash={clash} onClose={() => setClash(null)} />}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// 08 «Карточка записи» — the handoff's Appointment Card, via the mockup (D-21)

const SEGMENTS: { status: AppointmentStatus; key: string; st: string }[] = [
  { status: 'booked', key: 'card.booked', st: 'booked' },
  { status: 'confirmed', key: 'card.confirmed', st: 'confirmed' },
  { status: 'completed', key: 'card.completed', st: 'done' },
  { status: 'no_show', key: 'card.no_show', st: 'noshow' },
];

function AppointmentCard({ appt, onClose }: { appt: Appointment; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const refresh = useRefreshCalendar();
  const warn = useWarn();
  const client = useQuery({ queryKey: ['client', appt.client_id], queryFn: () => api.client(appt.client_id) });
  const [confirming, setConfirming] = useState(false);
  const [extending, setExtending] = useState(false);
  const [clash, setClash] = useState<Clash | null>(null);
  const [error, setError] = useState<string | null>(null);
  const now = new Date();
  const allowed = allowedNext(appt.status, appt.starts_at, now);
  const service = serviceName(appt.service_name_ru, appt.service_name_ky, locale);

  const fail = (err: unknown) => setError(errorText(err instanceof ApiError ? err.code : 'fallback'));

  async function setStatus(status: AppointmentStatus) {
    if (status === appt.status || !allowed.includes(status)) return;
    try {
      await api.updateAppointment(appt.id, { status });
      refresh();
    } catch (err) {
      fail(err);
    }
  }

  async function extend(by: number, ack = false) {
    const ends = new Date(Date.parse(appt.ends_at) + by * 60_000).toISOString();
    try {
      const saved = await api.updateAppointment(appt.id, { ends_at: ends, overlap_ack: ack });
      setExtending(false);
      setClash(null);
      refresh();
      warn(saved);
    } catch (err) {
      const c = clashFrom(err, appt.starts_at, ends, async (o) => {
        if (o.start) {
          // «Перенести на …» for an extension: move the visit to the free start, same length.
          const len = Date.parse(ends) - Date.parse(appt.starts_at);
          await api.updateAppointment(appt.id, { starts_at: o.start, ends_at: new Date(Date.parse(o.start) + len).toISOString() });
          setClash(null);
          setExtending(false);
          refresh();
        } else await extend(by, true);
      });
      if (c) {
        setExtending(false); // the conflict sheet replaces the «Продлить на» sheet
        setClash(c);
      } else fail(err);
    }
  }

  async function cancel() {
    try {
      await api.cancelAppointment(appt.id);
      refresh();
      navigate(`/day/${ymd(appt.starts_at)}`, { replace: true });
    } catch (err) {
      fail(err);
    }
  }

  return (
    <div className={`apc${confirming ? ' confirming' : ''}`}>
      <div className="apc-head">
        <span className="apc-av">{appt.client_name.charAt(0)}</span>
        <span className="grow" style={{ minWidth: 0 }}>
          <span className="apc-nm">{appt.client_name}</span>
          <span className="apc-sub">
            {service} · {minutesBetween(appt.starts_at, appt.ends_at)} {t('dur.min')}
          </span>
        </span>
        <button type="button" className="apc-x" aria-label={t('app.close')} onClick={onClose}>
          ×
        </button>
      </div>
      <div className="apc-client">
        <span className="ell">
          {client.data ? t('card.visits', { visits: client.data.visit_count, noShows: client.data.no_show_count }) : ' '}
        </span>
        <Link to={`/clients/${appt.client_id}`}>{t('card.client')}</Link>
      </div>
      <div className="apc-time">
        <span>
          <span className="apc-big">
            {hm(appt.starts_at)} — {hm(appt.ends_at)}
          </span>
          <span className="apc-sub">{dayTitle(ymd(appt.starts_at), locale)}</span>
        </span>
        <span className="apc-price">{formatMoney(appt.price, locale)}</span>
      </div>
      {error && <div className="warnbox" role="alert">{error}</div>}
      {appt.status === 'cancelled' ? (
        <div className="warnbox">{t('card.cancelled')}</div>
      ) : (
        <div>
          <span className="apc-cap">{t('card.status')}</span>
          <div className="apc-seg">
            {SEGMENTS.map((s) => (
              <button
                key={s.status}
                type="button"
                data-st={s.st}
                aria-pressed={appt.status === s.status}
                disabled={appt.status !== s.status && !allowed.includes(s.status)}
                onClick={() => void setStatus(s.status)}
              >
                {t(s.key)}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Messaging is Phase 2: drawn, disabled (D-40). */}
      <button type="button" className="apc-pri" disabled>
        <Icon.chatpill />
        <span>{t('card.whatsapp')}</span>
        <span className="soon">{t('app.soon')}</span>
      </button>
      <div className="apc-tiles">
        <button type="button" className="apc-tile" disabled={appt.status === 'cancelled'} onClick={() => navigate(`/appointments/new?move=${appt.id}`)}>
          <Icon.move />
          <span>{t('card.move')}</span>
        </button>
        <button type="button" className="apc-tile" disabled={appt.status === 'cancelled'} onClick={() => setExtending(true)}>
          <Icon.extend />
          <span>{t('card.extend')}</span>
        </button>
        <button type="button" className="apc-tile" onClick={() => navigate(`/appointments/new?client=${appt.client_id}&service=${appt.service_id}&date=${addDays(ymd(new Date()), 1)}`)}>
          <Icon.repeat />
          <span>{t('card.repeat')}</span>
        </button>
      </div>
      <div className="apc-rem" aria-disabled="true" style={{ opacity: 0.5 }}>
        <Icon.tick />
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('card.reminders')} <span className="soon">{t('app.soon')}</span>
        </span>
      </div>
      {allowed.includes('cancelled') && (
        <>
          <button type="button" className="apc-cancel" onClick={() => setConfirming(true)}>
            {t('card.cancel')}
          </button>
          <div className="apc-confirm">
            <b>{t('card.cancelAsk', { name: appt.client_name })}</b>
            {/* D-45: the WhatsApp half of this sentence waits for Phase 2. */}
            <span className="apc-sub">{t('card.cancelFrees', { time: hm(appt.starts_at) })}</span>
            <div className="btnrow">
              <button type="button" className="btn" onClick={() => setConfirming(false)}>
                {t('card.keep')}
              </button>
              <button type="button" className="btn kill" onClick={() => void cancel()}>
                {t('card.confirmCancel')}
              </button>
            </div>
          </div>
        </>
      )}
      {extending && !clash && (
        <Sheet onClose={() => setExtending(false)}>
          <h3>{t('card.extendBy')}</h3>
          {/* D-46: 10 / 15 / 30 minutes. */}
          <div className="btnrow">
            {[10, 15, 30].map((m) => (
              <button key={m} type="button" className="btn" onClick={() => void extend(m)}>
                +{m} {t('dur.min')}
              </button>
            ))}
          </div>
        </Sheet>
      )}
      {clash && <ConflictSheet clash={clash} onClose={() => setClash(null)} />}
    </div>
  );
}

export function AppointmentScreen() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const q = useQuery({ queryKey: ['appointment', id], queryFn: () => api.getAppointment(id) });
  const date = useMemo(() => (q.data ? ymd(q.data.starts_at) : ymd(new Date())), [q.data]);
  const close = () => navigate(`/day/${date}`);
  if (q.error) {
    return (
      <Shell active="calendar">
        <AppBar title={t('screen.appointment')} back={() => navigate(-1)} />
        <p className="placeholder">{errorText(q.error instanceof ApiError ? q.error.code : 'fallback')}</p>
      </Shell>
    );
  }
  return (
    <Shell active="calendar">
      {/* desktop: the card is the right-hand pane; phone: a sheet over the day (mockup 08) */}
      <DayBackdrop
        date={date}
        title={dayTitleShort(date, i18n.language as Locale)}
        side={desktop && q.data ? (
          <div className="pane pane-2">
            <AppointmentCard appt={q.data} onClose={close} />
          </div>
        ) : null}
      />
      {!desktop && q.data && (
        <Sheet onClose={close} className="apsheet">
          <AppointmentCard appt={q.data} onClose={close} />
        </Sheet>
      )}
    </Shell>
  );
}
